import {
    makeStyles,
    Menu,
    MenuGroup,
    MenuGroupHeader,
    MenuItemRadio,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Toolbar,
    ToolbarButton,
} from '@fluentui/react-components';
import {
    ChevronDoubleLeftRegular,
    ChevronDoubleRightRegular,
    ChevronDownRegular,
    ChevronLeftRegular,
    ChevronRightRegular,
} from '@fluentui/react-icons';
import { useCallback } from 'react';
import type { IQueryListRouteApiProps } from '../QueryListRouteApi';
import type {
    listPaginationQueryFragment$data,
    listPaginationQueryFragment$key,
} from './__generated__/listPaginationQueryFragment.graphql';
import { useListPaginationQueryFragment } from './list-pagination-query';

type TPagination = Pick<
    listPaginationQueryFragment$data,
    // | 'hasContent'
    | 'hasNext'
    | 'hasPrevious'
    | 'isFirst'
    | 'isLast'
    | 'pageNum'
    | 'pageSize'
    // | 'totalElements'
    | 'totalPages'
>;

interface IListPaginationProps extends IQueryListRouteApiProps {
    fragmentRef?: listPaginationQueryFragment$key;
    // onChange: (pageNum: number, pageSize: number, replace?: boolean) => void;
    // page: TPage | null | undefined;
    // query: () => TPage | null | undefined;
    // page: () => number | undefined,
    // size: () => (pageSize: number) => void
    // to: () => (pageNum: number, replace?: boolean) => void
}

const defaultPagination: TPagination = {
    // hasContent: false,
    hasNext: false,
    hasPrevious: false,
    isFirst: true,
    isLast: true,
    pageNum: 0,
    pageSize: 25,
    // totalElements: 0,
    totalPages: 1,
};

export const ListPagination = (props: IListPaginationProps) => {
    const styles = useStyles();
    const navigate = props.routeApi.useNavigate();
    const pagination = useListPaginationQueryFragment(props.fragmentRef) || defaultPagination;
    const { pageNum, pageSize, totalPages } = pagination;

    const onChange = useCallback(
        (pageNum: number, pageSize: number, replace?: boolean): void => {
            navigate({
                replace,
                search: (prevSearch) =>
                    Object.assign(Object.assign({}, prevSearch), {
                        pageNum,
                        pageSize,
                    }),
            });
        },
        [navigate]
    );

    return (
        <Toolbar className={styles.root} size="small">
            <ToolbarButton
                aria-label="Erste Seite"
                disabled={pagination.isFirst}
                icon={<ChevronDoubleLeftRegular />}
                onClick={() => onChange(0, pageSize)}
                title="Erste Seite"
            />

            <ToolbarButton
                aria-label="Vorherige Seite"
                disabled={!pagination.hasPrevious}
                icon={<ChevronLeftRegular />}
                onClick={() =>
                    onChange(
                        pageNum - 1,
                        pageSize
                        /*pageNum >= totalPages
                                ? totalPages -1
                                : pageNum - 1,
                            pageSize,
                            true*/
                    )
                }
                title="Vorherige Seite"
            />

            <Menu
                checkedValues={{ pageNum: [pageNum as unknown as string] }}
                inline={true}
                onCheckedValueChange={(_event, data) => {
                    const targetPageNum = data.checkedItems[0] as unknown as number;
                    if (pageNum !== targetPageNum) {
                        onChange(targetPageNum, pageSize);
                    }
                }}
            >
                <MenuTrigger disableButtonEnhancement={true}>
                    <ToolbarButton
                        aria-label="Seiten"
                        // children={`Seite ${pageNumber + 1} von ${totalPages}`}
                        className={styles.menuTriggerButton}
                        icon={<ChevronDownRegular />}
                        // @ts-ignore
                        iconPosition="after"
                        title="Seiten"
                    >
                        Seite {pageNum + 1}
                    </ToolbarButton>
                </MenuTrigger>

                <MenuPopover>
                    <MenuList>
                        <MenuGroup>
                            <MenuGroupHeader>Seiten</MenuGroupHeader>

                            {new Array(totalPages).fill(undefined).map((_val, index) => (
                                <MenuItemRadio
                                    key={index}
                                    name="pageNum"
                                    value={index as unknown as string}
                                >
                                    Seite {index + 1}
                                </MenuItemRadio>
                            ))}
                        </MenuGroup>
                    </MenuList>
                </MenuPopover>
            </Menu>

            <ToolbarButton
                aria-label="Nächste Seite"
                disabled={!pagination.hasNext}
                icon={<ChevronRightRegular />}
                onClick={() => onChange(pageNum + 1, pageSize)}
                title="Nächste Seite"
            />

            <ToolbarButton
                aria-label="Letzte Seite"
                disabled={pagination.isLast}
                icon={<ChevronDoubleRightRegular />}
                onClick={() => onChange(totalPages - 1, pageSize)}
                title="Letzte Seite"
            />

            <Menu
                checkedValues={{ pageSize: [pageSize as unknown as string] }}
                inline={true}
                onCheckedValueChange={(_event, data) => {
                    onChange(pageNum, data.checkedItems[0] as unknown as number);
                }}
            >
                <MenuTrigger disableButtonEnhancement={true}>
                    <ToolbarButton
                        aria-label="Einträge pro Seite"
                        // children={`Seite ${pageNumber + 1} von ${totalPages}`}
                        className="unset-min-width"
                        icon={<ChevronDownRegular />}
                        // @ts-ignore
                        iconPosition="after"
                        title="Einträge pro Seite"
                    >
                        {pageSize}
                    </ToolbarButton>
                </MenuTrigger>

                <MenuPopover
                // TODO: unset min width
                >
                    <MenuList>
                        <MenuGroup>
                            <MenuGroupHeader>Einträge pro Seite</MenuGroupHeader>

                            {entriesPerPage}
                        </MenuGroup>
                    </MenuList>
                </MenuPopover>
            </Menu>
        </Toolbar>
    );
};

const entriesPerPage = [25, 50, 100].map((value) => (
    <MenuItemRadio key={value} name="pageSize" value={value as unknown as string}>
        {value}
    </MenuItemRadio>
));

const useStyles = makeStyles({
    menuTriggerButton: {
        whiteSpace: 'nowrap',
    },
    root: {
        padding: 0,
        zIndex: 1,
    },
});
